import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Chip,
  Divider
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as AudienceIcon,
  AttachMoney as BudgetIcon,
  Theaters as DistributionIcon
} from '@mui/icons-material';
import { brandColors } from '../../theme/theme';

/**
 * ExecutiveSummaryCard Component
 * 
 * Displays at-a-glance executive summary with key metrics for quick decision-making.
 * Shows score, recommendation, top strengths/weaknesses, and critical metrics.
 */

const ExecutiveSummaryCard = ({ data, scoreColor, onExpandDetails }) => {
  // Parse top 3 strengths and weaknesses
  const strengths = parseTopItems(data.sections?.strengths || '', 3);
  const weaknesses = parseTopItems(data.sections?.weaknesses_and_concerns || '', 3);
  
  // Extract key metrics
  const targetAudience = extractTargetAudience(data.sections?.market_analysis || '');
  const budgetRec = extractBudgetRecommendation(data.sections?.distribution_strategy || '');
  const distributionFit = extractDistributionFit(data.sections?.market_analysis || '');

  return (
    <Paper
      elevation={4}
      sx={{
        p: 4,
        mb: 3,
        background: `linear-gradient(135deg, ${brandColors.white} 0%, ${scoreColor}08 100%)`,
        borderLeft: `6px solid ${scoreColor}`,
        borderRadius: 2
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ color: brandColors.primary, fontWeight: 700 }}>
          Executive Summary
        </Typography>
        <Chip
          label="Quick Decision Guide"
          size="small"
          sx={{
            backgroundColor: `${brandColors.secondary}15`,
            color: brandColors.secondary,
            fontWeight: 600
          }}
        />
      </Box>

      <Grid container spacing={3}>
        {/* Left Column - Score & Recommendation */}
        <Grid item xs={12} md={4}>
          <Box sx={{ textAlign: 'center' }}>
            {/* Mini Score Gauge */}
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: '50%',
                border: `6px solid ${scoreColor}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mx: 'auto',
                mb: 2,
                background: `${scoreColor}15`
              }}
            >
              <Typography variant="h3" sx={{ color: scoreColor, fontWeight: 700 }}>
                {data.overall_score}
              </Typography>
            </Box>
            
            {/* Recommendation */}
            <Chip
              icon={data.overall_score >= 70 ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={data.recommendation}
              sx={{
                backgroundColor: scoreColor,
                color: brandColors.white,
                fontWeight: 600,
                fontSize: '0.9rem',
                px: 1,
                py: 2
              }}
            />
          </Box>
        </Grid>

        {/* Middle Column - Key Strengths */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ color: brandColors.primary, fontWeight: 600, mb: 1.5 }}>
            Top Strengths
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {strengths.map((strength, index) => (
              <Chip
                key={index}
                icon={<CheckIcon />}
                label={strength}
                size="small"
                sx={{
                  justifyContent: 'flex-start',
                  backgroundColor: '#388e3c15',
                  color: '#388e3c',
                  '& .MuiChip-icon': { color: '#388e3c' },
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    fontSize: '0.8rem'
                  }
                }}
              />
            ))}
          </Box>
        </Grid>

        {/* Right Column - Key Concerns */}
        <Grid item xs={12} md={4}>
          <Typography variant="subtitle2" sx={{ color: brandColors.primary, fontWeight: 600, mb: 1.5 }}>
            Top Concerns
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            {weaknesses.map((weakness, index) => (
              <Chip
                key={index}
                icon={<WarningIcon />}
                label={weakness}
                size="small"
                sx={{
                  justifyContent: 'flex-start',
                  backgroundColor: '#f57c0015',
                  color: '#f57c00',
                  '& .MuiChip-icon': { color: '#f57c00' },
                  '& .MuiChip-label': {
                    whiteSpace: 'normal',
                    textAlign: 'left',
                    fontSize: '0.8rem'
                  }
                }}
              />
            ))}
          </Box>
        </Grid>
      </Grid>

      <Divider sx={{ my: 3 }} />

      {/* Key Metrics Row */}
      <Grid container spacing={2}>
        {targetAudience && (
          <Grid item xs={12} sm={4}>
            <MetricCard
              icon={<AudienceIcon />}
              label="Target Audience"
              value={targetAudience}
              color={brandColors.secondary}
            />
          </Grid>
        )}
        
        {budgetRec && (
          <Grid item xs={12} sm={4}>
            <MetricCard
              icon={<BudgetIcon />}
              label="Budget Recommendation"
              value={budgetRec}
              color="#388e3c"
            />
          </Grid>
        )}
        
        {distributionFit && (
          <Grid item xs={12} sm={4}>
            <MetricCard
              icon={<DistributionIcon />}
              label="Best Distribution"
              value={distributionFit}
              color={brandColors.primary}
            />
          </Grid>
        )}
      </Grid>
    </Paper>
  );
};

// Helper Component
const MetricCard = ({ icon, label, value, color }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1.5,
      p: 1.5,
      backgroundColor: `${color}08`,
      borderRadius: 1,
      border: `1px solid ${color}30`
    }}
  >
    <Box sx={{ color: color }}>{icon}</Box>
    <Box sx={{ flex: 1 }}>
      <Typography variant="caption" sx={{ color: brandColors.primary, opacity: 0.7, display: 'block', fontSize: '0.7rem' }}>
        {label}
      </Typography>
      <Typography variant="body2" sx={{ color: brandColors.primary, fontWeight: 600, fontSize: '0.85rem' }}>
        {value}
      </Typography>
    </Box>
  </Box>
);

// Helper Functions
function parseTopItems(text, count = 3) {
  const items = [];
  const lines = text.split('\n');
  
  for (const line of lines) {
    // Match numbered items like "1. **Text**:" or "1. Text"
    const match = line.match(/^\s*\d+\.\s+\*\*(.+?)\*\*:?/);
    if (match) {
      items.push(match[1].trim());
    }
  }
  
  return items.slice(0, count);
}

function extractTargetAudience(text) {
  const match = text.match(/\*\*Primary:\*\*\s*(.+?)(?:\n|\*\*)/s);
  if (match) {
    return match[1].trim().substring(0, 60) + (match[1].length > 60 ? '...' : '');
  }
  return null;
}

function extractBudgetRecommendation(text) {
  // Look for budget ranges like "$15-30M" or "$40M+"
  const match = text.match(/\$[\d,]+-?\$?[\d,]*M\+?/);
  return match ? match[0] : null;
}

function extractDistributionFit(text) {
  // Extract which distribution is recommended
  if (text.toLowerCase().includes('streaming') && text.toLowerCase().includes('optimal')) {
    return 'Streaming';
  }
  if (text.toLowerCase().includes('theatrical') && text.toLowerCase().includes('potential')) {
    return 'Theatrical';
  }
  if (text.toLowerCase().includes('hybrid')) {
    return 'Hybrid';
  }
  return 'See Analysis';
}

export default ExecutiveSummaryCard;

